/**
 * CloudMeshField — clouds as REAL 3D MESHES in the world, not a screen-space
 * composite.
 *
 * WHY (2026-08-19 field directive): the post-pass raymarch slab (CloudRenderer)
 * is a screen-space effect — even with physically-correct world anchoring
 * (parallaxScale 1) a 3.8 km deck parallaxes only ~0.75° per 50 m walked
 * (moon-follows-the-car), and players kept reading it as "the clouds follow
 * my camera". This module instead spawns a pooled set of sphere meshes at
 * world positions derived from the weather map; each mesh raymarches the
 * engine's baked 3D noise INSIDE ITS OWN VOLUME in the fragment shader (the
 * classic volume-renderer "raymarch in a sphere" pattern — camera ray vs
 * analytic sphere bound, march between entry/exit). Because they are scene
 * geometry they get:
 *   - TRUE parallax for free (walk 50 m and a 900 m-altitude puff swings
 *     ~3° against the towers — unmistakably stationary in the world);
 *   - depth-tested occlusion against buildings both ways;
 *   - natural frustum culling + transparent sorting from three.js.
 *
 * Architecture:
 *   - CPU "chunk streamer": the field radius around the camera is divided
 *     into cells (default 700 m); each cell is deterministically hashed to a
 *     cloud (position jitter / radius / altitude / noise seed) and sampled
 *     against the CPU copy of the weather map. Cells that pass the coverage
 *     gate (nearest-first, up to maxClouds) get a pooled mesh. The mapping
 *     is stable as the camera moves, so clouds persist when you leave and
 *     return — they are world objects.
 *   - Wind scrolls the noise INSIDE each volume (same convention as the
 *     deck: metres of pattern drift), plus the net-zero evolution counter-
 *     drift morphs shapes in place. Meshes themselves stay at their cell
 *     home, so pooling stays trivial and rebase-safe.
 *   - FP64 origin rebase: shader noise UVs use ABSOLUTE world position
 *     (positionWorld − worldOffset, worldOffset = scene.position — the same
 *     convention as CloudRenderer); mesh centers are re-fed per frame, so a
 *     rebase snap moves clouds WITH the world, never re-arranging them.
 *
 * Anti-pop: new assignments fade in over ~3 s (per-mesh uBirth) and everything
 * beyond fadeStart is fully transparent, so recycles at the field edge are
 * invisible.
 *
 * Blending: premultiplied OVER — the march outputs the same {rgb premult, a}
 * contract as CloudRenderer; the material uses CustomBlending One / OneMinus-
 * SrcAlpha so `final = rgb + dst·(1−a)` exactly like the post-pass composite.
 * depthWrite off, BackSide (so the fragment position is the sphere EXIT point
 * and camera-inside-volume still rasterizes), depthTest on (towers occlude).
 *
 * v1 simplifications (documented, deliberate):
 *   - No scene-depth clamp inside the march: a tower poking INTO a volume
 *     slices it hard (the old sky-mask composite had the same hard edge).
 *     Soft-depth fade via viewportDepthTexture is the roadmap item.
 *   - Sphere bound (not ellipsoid); flat/wide shapes come from the noise
 *     anisotropy + height envelope, not the mesh scale.
 *   - Wind scrolls the pattern; meshes don't translate. Keeps cell pooling
 *     stable; reads identically at cloud distances.
 */

import * as THREE from 'three/webgpu';
import { markMrtOverlay } from '../post/mrtOverlay.js';
import {
  Fn, If, Loop, vec2, vec3, vec4, float, uniform, uint,
  exp, max, min, abs, step, int, clamp, dot, mix, fract, sin,
  pow, normalize, length, distance, smoothstep, texture3D, texture, uniformArray,
  screenCoordinate, positionWorld, cameraPosition,
} from 'three/tsl';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { getBlueNoiseTexture } from '../bluenoise/BlueNoiseRuntime.js';
import { cloudLobes, MAX_LOBES, LOBE_SMIN_K, type CloudLobe } from './CloudLobes.js';

export interface CloudMeshFieldConfig {
  /** Half-width of the cloud field around the camera (m). Default 9000. */
  fieldRadius?: number;
  /** Streaming cell size (m). Default 700. */
  cellSize?: number;
  /** Max simultaneous cloud meshes (pool size). Default 40. */
  maxClouds?: number;
  /**
   * The CONDENSATION LEVEL (m): every cloud's flat BASE sits at this
   * altitude (±baseJitter). Real cumulus bases align at the lifting
   * condensation level — "neighbouring cumulus develop bases at similar
   * levels" — while TOPS vary with cloud type; randomizing center
   * altitudes (the first cut) scattered the bases and read as balloons.
   * Default 750.
   */
  baseAltitude?: number;
  /** Per-cloud base-altitude jitter (m). Default 40. */
  baseJitter?: number;
  /** Cloud bounding-sphere radius range (m). Default [220, 520]. */
  radiusMin?: number;
  radiusMax?: number;
  /**
   * 3D noise UV per metre inside a volume. Default 0.0038 (~260 m base
   * period) — a 400-500 m puff spans ~2 lobes, so clouds read as cauliflower
   * clusters, not single smooth balls (the 0.0016 first cut put under one
   * noise period across a volume ⇒ balloon shapes in the mesh bed).
   */
  noiseScale?: number;
  /** Weather-map UV per metre for CPU placement. Default 0.0001 (10 km tile). */
  weatherScale?: number;
  /** Extinction (1/m). Default 0.04 — softer than the deck's 0.05 so the
   *  smaller volumes keep translucent edges instead of chalk-flat interiors. */
  extinction?: number;
  /** Primary march steps per fragment. Default 20. */
  steps?: number;
  /** Sun-cone samples per non-zero primary sample. Default 2. */
  sunSteps?: number;
  /** Fraction of fieldRadius where the distance fade starts. Default 0.72. */
  fadeStartRatio?: number;
  /** Wind (m/s, pattern scroll) + time scale — inherited from the module. */
  windX?: number;
  windZ?: number;
  windTimeScale?: number;
  /** Net-zero shape evolution speed (m/s). Default 1.5 (matches the deck). */
  evolveSpeed?: number;
  /**
   * CONDENSATION (2026-09-10): a cloud born INSIDE the visible field (a
   * coverage rise, a regime change) grows over this many seconds — the
   * subtractive gate starts high so only the densest cores survive as wisps,
   * then relaxes until the turrets fill in. Real cumulus condense over
   * minutes; a 3 s alpha fade read as "the mesh just forms". Births at the
   * field's far edge keep the short fade (they cross-fade with the impostor
   * layer there). Default 40.
   */
  growSeconds?: number;
  /**
   * March-step multiplier for the field's farthest clouds (linear from 1 at
   * 0.35·fieldRadius to this at fieldRadius): a cloud 4 km out spans a few
   * dozen pixels and needs a third of the samples. Default 2.5.
   */
  lodFarStep?: number;
  /**
   * The far LOD (`CloudImpostorLayer`): instanced sprites carrying the SAME
   * weather lattice from the field's fade band out to `farRadius`, cross-
   * faded with the volumetric clouds. `false` disables it; defaults apply
   * when omitted. Consumed by CloudModule.
   */
  impostors?: CloudImpostorLayerConfig | false;
}

/** Far-LOD sprite layer configuration (see CloudImpostorLayer). */
export interface CloudImpostorLayerConfig {
  /** Sprites on the field's 700 m lattice out to here (m). Default 16000. */
  midRadius?: number;
  /** Coarser far lattice (cellSize·farCellScale) out to here (m). Default 60000. */
  farRadius?: number;
  /** Far-lattice cell multiplier. Default 4. */
  farCellScale?: number;
  /** Instance pool. Default 1600. */
  maxSprites?: number;
}

export interface CloudMeshFieldOptions {
  config?: CloudMeshFieldConfig;
  baseNoise: THREE.Data3DTexture;
  detailNoise: THREE.Data3DTexture;
  weatherMap: THREE.DataTexture;
}

// ─── Pure CPU helpers (unit-tested) ─────────────────────────────────────────

/** Deterministic 2D→1D cell hash in [0, 1). */
export function hashCell(cx: number, cz: number, salt: number): number {
  let h = (cx * 374761393 + cz * 668265263 + salt * 2246822519) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Global-coverage → per-cell weather.r spawn threshold. */
export function coverageThreshold(coverage: number): number {
  return Math.max(0.25, Math.min(0.95, 1.1 - coverage * 1.4));
}

/**
 * Bilinear sample of the weather texture's R (coverage) / G (type) channels
 * at absolute world (x, z), RepeatWrapping. `data` is the RGBA8 backing array
 * of the generated weather DataTexture.
 */
export function sampleWeatherCpu(
  data: Uint8Array, size: number, x: number, z: number, weatherScale: number,
): { r: number; g: number } {
  const u = ((x * weatherScale) % 1 + 1) % 1;
  const v = ((z * weatherScale) % 1 + 1) % 1;
  const fx = u * size - 0.5;
  const fz = v * size - 0.5;
  const x0 = Math.floor(fx), z0 = Math.floor(fz);
  const tx = fx - x0, tz = fz - z0;
  const wrap = (i: number) => ((i % size) + size) % size;
  const px = (ix: number, iz: number, ch: number) =>
    data[(wrap(iz) * size + wrap(ix)) * 4 + ch] / 255;
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
  const r = lerp(
    lerp(px(x0, z0, 0), px(x0 + 1, z0, 0), tx),
    lerp(px(x0, z0 + 1, 0), px(x0 + 1, z0 + 1, 0), tx), tz);
  const g = lerp(
    lerp(px(x0, z0, 1), px(x0 + 1, z0, 1), tx),
    lerp(px(x0, z0 + 1, 1), px(x0 + 1, z0 + 1, 1), tx), tz);
  return { r, g };
}

export interface DesiredCell {
  key: string;
  cx: number;
  cz: number;
  dist: number;
}

// ─── Cloud genera (étage archetypes) ────────────────────────────────────────

export type CloudLayerKind = 'cumulus' | 'stratocumulus' | 'altocumulus' | 'cirrus';

/**
 * Per-genus morphology + shading parameters, researched against the WMO
 * étage classification:
 *   cumulus       (low)  — cellular, flat base, rounded vertical tops
 *   stratocumulus (low)  — clumped layer of joined lumps, thick/thin, gray
 *   altocumulus   (mid)  — patches of MANY small separate globular cloudlets
 *   cirrus        (high) — hair-like translucent streaks aligned to the wind
 */
export interface CloudArchetype {
  radiusMin: number;
  radiusMax: number;
  /** Ellipsoid flattening range (lerped by type01 = weather.g). */
  flattenMin: number;
  flattenMax: number;
  /** Density multiplier (stratocumulus > 1 ⇒ self-shadowed gray). */
  density: number;
  /** Fringe-erosion multiplier (cirrus high ⇒ wispy shreds). */
  erode: number;
  /** Wind-aligned noise anisotropy (0 = isotropic; cirrus ~3 ⇒ streaks). */
  stretch: number;
  /** Streaming cell-size multiplier (<1 ⇒ denser packing / more, smaller). */
  cellScale: number;
  /** Base-altitude jitter (m). */
  baseJitter: number;
}

export const CLOUD_ARCHETYPES: Record<CloudLayerKind, CloudArchetype> = {
  cumulus: {
    radiusMin: 220, radiusMax: 560, flattenMin: 0.45, flattenMax: 0.85,
    // baseJitter 40→14 (2026-08-27): real cumulus bases all sit AT the lifting
    // condensation level — a shared flat ceiling with tops of varied height is
    // THE cumulus signature, and the old ±40 m scatter erased it.
    density: 1.0, erode: 1.0, stretch: 0, cellScale: 1.0, baseJitter: 14,
  },
  stratocumulus: {
    radiusMin: 420, radiusMax: 720, flattenMin: 0.26, flattenMax: 0.4,
    density: 1.35, erode: 0.65, stretch: 0, cellScale: 0.85, baseJitter: 12,
  },
  altocumulus: {
    radiusMin: 110, radiusMax: 200, flattenMin: 0.3, flattenMax: 0.45,
    density: 0.9, erode: 0.85, stretch: 0, cellScale: 0.42, baseJitter: 60,
  },
  cirrus: {
    radiusMin: 520, radiusMax: 950, flattenMin: 0.06, flattenMax: 0.1,
    density: 0.26, erode: 1.5, stretch: 3, cellScale: 1.6, baseJitter: 120,
  },
};

/** One active étage of the sky: which genus, where, how much, what pool share. */
export interface CloudLayerSpec {
  kind: CloudLayerKind;
  /** The layer's condensation level (m) — every base in the layer aligns here. */
  baseAltitude: number;
  /** Multiplier on the global coverage for this layer's spawn threshold. */
  coverage: number;
  /** Fraction of the mesh pool this layer may claim (shares are normalized). */
  share: number;
}

export interface CloudFormation {
  layers: CloudLayerSpec[];
}

/**
 * Weather → formation (unit-tested), following the observed frontal
 * sequences: fair skies carry scattered cumulus + sparse cirrus; humid
 * pre-frontal skies add altocumulus patches and thickening cirrus; active
 * precipitation flips the low deck to gray joined stratocumulus with an
 * altocumulus veil and hides the high étage entirely.
 *
 * `coverage` (0..1) selects the REGIME; each layer's `coverage` field is a
 * MULTIPLIER on the field's global coverage (the field computes
 * layerCoverage = global · multiplier — passing absolutes here would apply
 * the sky knob twice). `precip01` = precipitation intensity (0 = dry).
 * Étage base altitudes are the caller's (game-scale).
 */
export function formationForWeather(
  coverage: number, precip01: number,
  alts: { low: number; mid: number; high: number },
): CloudFormation {
  const cov = Math.max(0, Math.min(1, coverage));
  const wet = Math.max(0, Math.min(1, precip01));
  if (wet > 0.05) {
    // Rain/snow: nimbo-like joined gray deck; no visible high étage.
    return {
      layers: [
        { kind: 'stratocumulus', baseAltitude: alts.low * 0.9, coverage: 1.5, share: 0.62 },
        { kind: 'altocumulus', baseAltitude: alts.mid, coverage: 0.7, share: 0.38 },
      ],
    };
  }
  if (cov > 0.55) {
    // Humid / pre-frontal: mediocris + altocumulus patches + thick cirrus.
    return {
      layers: [
        { kind: 'cumulus', baseAltitude: alts.low, coverage: 1, share: 0.5 },
        { kind: 'altocumulus', baseAltitude: alts.mid, coverage: 0.8, share: 0.3 },
        { kind: 'cirrus', baseAltitude: alts.high, coverage: 0.9, share: 0.2 },
      ],
    };
  }
  // Fair weather: scattered cumulus, a few altocumulus, sparse cirrus.
  return {
    layers: [
      { kind: 'cumulus', baseAltitude: alts.low, coverage: 1, share: 0.6 },
      { kind: 'altocumulus', baseAltitude: alts.mid, coverage: 0.5, share: 0.15 },
      { kind: 'cirrus', baseAltitude: alts.high, coverage: 0.75, share: 0.25 },
    ],
  };
}

export interface CloudShape {
  /** Horizontal semi-axis (m). */
  radius: number;
  /** Vertical/horizontal ratio (ellipsoid flattening). */
  flatten: number;
  /** Vertical semi-axis (m) = radius·flatten. */
  ry: number;
  /** Ellipsoid center altitude (m) — base + ry, so the BOTTOM sits at base. */
  centerY: number;
  /** This cloud's base altitude (m). */
  baseY: number;
  /** Dome height (m) over the base = 2·ry: the mesh is the UPPER HALF of the
   *  ellipsoid with the flat base through its equator (2026-09-10). */
  height: number;
}

/**
 * Genus morphology (unit-tested). `type01` = weather.g lerps size and
 * flattening inside the genus archetype (cumulus: humilis → mediocris/
 * congestus). All bases in a layer sit at its condensation level (±jitter)
 * — tops vary.
 */
export function computeCloudShape(
  sizeHash01: number, type01: number, jitterHash01: number,
  cfg: { baseAltitude: number },
  arch: CloudArchetype = CLOUD_ARCHETYPES.cumulus,
): CloudShape {
  // Species-like size spread (2026-08-27 variety fix): quadratic bias means
  // many modest humilis and the occasional towering congestus — the old
  // `0.3 + 0.5·h + 0.2·t` collapsed every cloud into a narrow middle band.
  const radius = arch.radiusMin +
    (arch.radiusMax - arch.radiusMin) * Math.min(1, 0.16 + 0.84 * sizeHash01 * sizeHash01 + 0.16 * type01);
  // Flatten keeps its type01 monotonic trend but gains a per-cloud wobble so
  // neighbours under the same weather no longer share one silhouette.
  const flatten = arch.flattenMin + (arch.flattenMax - arch.flattenMin) *
    Math.max(0, Math.min(1, type01 * 0.75 + (sizeHash01 - 0.5) * 0.35 + 0.08));
  const ry = radius * flatten;
  const baseY = cfg.baseAltitude + (jitterHash01 - 0.5) * 2 * arch.baseJitter;
  return { radius, flatten, ry, centerY: baseY + ry, baseY, height: ry * 2 };
}

/** Everything one cloud cell derives from its hashes + the weather — shared by the
 *  volumetric field and the impostor layer so a cloud keeps its place, size,
 *  character and turret layout across the LOD hand-off. Pure. */
export interface CloudCell {
  absX: number; absZ: number;
  shape: CloudShape;
  /** x = density mul, y = erosion mul, z = wind stretch. */
  params: { x: number; y: number; z: number };
  /** Noise seed offset (m) — whole noise tiles apart per cloud. */
  seed: { x: number; y: number; z: number };
  lobes: CloudLobe[];
  /** weather.g at the cell (the type). */
  type01: number;
}

export function deriveCloudCell(
  cx: number, cz: number, cellSize: number, spec: CloudLayerSpec, arch: CloudArchetype,
  weather: { data: Uint8Array; size: number }, weatherScale: number,
): CloudCell {
  const h1 = hashCell(cx, cz, 1), h2 = hashCell(cx, cz, 2), h3 = hashCell(cx, cz, 3);
  const h4 = hashCell(cx, cz, 4), h5 = hashCell(cx, cz, 5), h6 = hashCell(cx, cz, 6);
  const w = sampleWeatherCpu(weather.data, weather.size, (cx + 0.5) * cellSize, (cz + 0.5) * cellSize, weatherScale);
  const absX = (cx + 0.5 + (h1 - 0.5) * 0.7) * cellSize;
  const absZ = (cz + 0.5 + (h2 - 0.5) * 0.7) * cellSize;
  const shape = computeCloudShape(h3, w.g, h4, { baseAltitude: spec.baseAltitude }, arch);
  return {
    absX, absZ, shape,
    // Per-cloud CHARACTER (the "they all look the same" fix, 2026-08-27):
    // density and erosion each get a ±~30% personal spread around the genus.
    params: { x: arch.density * (0.78 + 0.5 * h5), y: arch.erode * (0.7 + 0.65 * h6), z: arch.stretch },
    // Seed jumps WHOLE NOISE TILES (the base texture tiles every ~263 m):
    // ×64 moved a cloud a quarter-tile — every cloud a window onto one lattice.
    seed: { x: h4 * 8192, y: h1 * 4096, z: h2 * 8192 },
    lobes: cloudLobes([h1, h2, h3, h4, h5, h6], w.g, spec.kind),
    type01: w.g,
  };
}

/**
 * The set of cells that should hold a cloud: every cell within `fieldRadius`
 * of (camX, camZ) whose weather coverage passes `threshold`, nearest-first,
 * capped at `maxClouds`. Deterministic for a given camera position — clouds
 * persist when the player leaves and returns.
 */
export function computeDesiredCells(
  camX: number, camZ: number, coverage: number,
  opts: {
    fieldRadius: number; cellSize: number; maxClouds: number; weatherScale: number;
    /** Weather-sampling offset (m) — decorrelates étages so layers don't stack. */
    weatherOffset?: { x: number; z: number };
    /** 70/30 near-first bucketing (walk-parallax legibility) — low étage only. */
    nearBias?: boolean;
    /** Key prefix so per-layer cell keys never collide in the shared pool. */
    keyPrefix?: string;
    /** Cells CURRENTLY holding a cloud — they pass a relaxed threshold and a
     *  slightly larger radius (hysteresis: a visible cloud never flickers off
     *  because the lerped coverage grazed the cut line). */
    sticky?: Set<string>;
  },
  weather: { data: Uint8Array; size: number },
): DesiredCell[] {
  const { fieldRadius, cellSize, maxClouds, weatherScale } = opts;
  const off = opts.weatherOffset ?? { x: 0, z: 0 };
  const prefix = opts.keyPrefix ?? '';
  const threshold = coverageThreshold(coverage);
  const ccx = Math.floor(camX / cellSize);
  const ccz = Math.floor(camZ / cellSize);
  const range = Math.ceil(fieldRadius / cellSize);
  const out: DesiredCell[] = [];
  for (let dx = -range; dx <= range; dx++) {
    for (let dz = -range; dz <= range; dz++) {
      const cx = ccx + dx, cz = ccz + dz;
      const wx = (cx + 0.5) * cellSize, wz = (cz + 0.5) * cellSize;
      const dist = Math.hypot(wx - camX, wz - camZ);
      const key = `${prefix}${cx},${cz}`;
      const held = opts.sticky?.has(key) ?? false;
      if (dist > fieldRadius * (held ? 1.06 : 1)) continue;
      const w = sampleWeatherCpu(weather.data, weather.size, wx + off.x, wz + off.z, weatherScale);
      if (w.r < threshold - (held ? 0.05 : 0)) continue;
      out.push({ key, cx, cz, dist });
    }
  }
  out.sort((a, b) => a.dist - b.dist);
  if (out.length <= maxClouds) return out;
  // STABLE over-budget selection: a per-cell hash priority. The old positional
  // stride (`out[i·len/max]`) re-rolled the ENTIRE layer whenever the candidate
  // count changed by one (every weather tick near the threshold) — a major
  // co-cause of the turn-the-camera popping. Hash priority means an appearing/
  // disappearing candidate displaces at most one selection, and held cells get
  // a priority bonus so selection churn favors keeping what's visible.
  const pick = (cands: DesiredCell[], n: number): DesiredCell[] =>
    cands
      .map((c) => ({ c, p: hashCell(c.cx, c.cz, 7) + (opts.sticky?.has(c.key) ? 0.35 : 0) }))
      .sort((a, b) => b.p - a.p)
      .slice(0, n)
      .map((x) => x.c)
      .sort((a, b) => a.dist - b.dist); // callers + tests expect nearest-first order
  if (opts.nearBias === false) {
    // Mid/high étages: even sky fill via the stable hash spread.
    return pick(out, maxClouds);
  }
  // Over budget: BUCKET the pool 70/30 near/far. Nearest-first alone spends
  // the whole pool within ~4 km and leaves the horizon empty (mesh bed
  // M6/M7); a pure distance stride thins the near field until walk-speed
  // parallax disappears (the 2026-08-19 regression: nearParallax MAD 10 →
  // 1.6). Near bucket = nearest-first within 0.45·fieldRadius (parallax
  // legibility lives here); far bucket = stride-spread over the rest
  // (horizon fill). A short near bucket hands its slots to the far bucket.
  const nearBudget = Math.ceil(maxClouds * 0.7);
  const nearEdge = opts.fieldRadius * 0.45;
  const near = out.filter((d) => d.dist <= nearEdge).slice(0, nearBudget);
  const farCands = out.filter((d) => d.dist > nearEdge);
  const farBudget = maxClouds - near.length;
  const far = pick(farCands, Math.min(farBudget, farCands.length));
  return [...near, ...far];
}

// ─── The field ──────────────────────────────────────────────────────────────

interface MeshCloudUniforms {
  center: ReturnType<typeof uniform>;
  radius: ReturnType<typeof uniform>;
  /** Retirement timestamp (uTime units); 1e9 = alive. Drives the fade-OUT —
   *  despawn used to be an instant `visible = false`, a one-frame pop. */
  death: ReturnType<typeof uniform>;
  /** Vertical/horizontal semi-axis ratio (ellipsoid flattening, ≤1). */
  flatten: ReturnType<typeof uniform>;
  /** Genus params: x = density mul, y = fringe-erosion mul, z = wind-aligned
   *  noise stretch (0 = isotropic; cirrus ~3 = hair-like streaks). */
  params: ReturnType<typeof uniform>;
  seed: ReturnType<typeof uniform>;
  birth: ReturnType<typeof uniform>;
  /** Condensation time (s): the subtractive gate relaxes from +0.6 to 0 over it. */
  grow: ReturnType<typeof uniform>;
  /** March-step multiplier by distance (1 near → lodFarStep at the field edge). */
  lod: ReturnType<typeof uniform>;
  /** The turret cluster (shape space): MAX_LOBES × (x, y, z, r). */
  lobes: ReturnType<typeof uniformArray>;
}

interface PoolEntry {
  mesh: THREE.Mesh;
  u: MeshCloudUniforms;
  cellKey: string | null;
  /** While > 0: retired but still fading out until this _elapsed time. */
  dyingUntil: number;
}

export class CloudMeshField {
  readonly group = new THREE.Group();
  /** Last quantized coverage bucket — the re-stream latch key (see setCoverage). */
  private _covBucket = -1;
  /** Cell keys currently holding a live cloud (the hysteresis sticky set). */
  private readonly _heldKeys = new Set<string>();
  /** Seconds a retired cloud takes to fade out (mirrors the 3 s birth ramp). */
  static readonly FADE_OUT_S = 2.5;
  /** Shared uniform nodes — single-point updates reach every mesh material.
   *  The night-ramp/weather drivers write these (same names as CloudUniforms). */
  readonly uniforms: {
    sunDirection: ReturnType<typeof uniform>;
    sunColor: ReturnType<typeof uniform>;
    sunIlluminance: ReturnType<typeof uniform>;
    ambientBottom: ReturnType<typeof uniform>;
    ambientTop: ReturnType<typeof uniform>;
    coverage: ReturnType<typeof uniform>;
    densityScale: ReturnType<typeof uniform>;
  };

  private readonly _cfg: Required<Omit<CloudMeshFieldConfig, never>>;
  private readonly _baseNoise: THREE.Data3DTexture;
  private readonly _detailNoise: THREE.Data3DTexture;
  private readonly _weatherData: Uint8Array;
  private readonly _weatherSize: number;
  private readonly _pool: PoolEntry[] = [];
  private _enabled = true;
  private _elapsed = 0;
  private _windX = 0;
  private _windZ = 0;
  private _evolve = 0;
  private _lastCellX = Number.NaN;
  /**
   * The last re-stream wanted more cells than the pool had free meshes
   * (dying meshes are excluded — they keep rendering their fade-out). WHY:
   * a re-stream only ran on a cell crossing, so after a teleport (all 40
   * dying at once) the sky stayed EMPTY until the camera crossed the next
   * 700 m cell, and in steady walking the entering column was always one
   * crossing behind. A park (fade done) now re-arms the latch (2026-09-12).
   */
  private _starved = false;
  private _lastCellZ = Number.NaN;
  private _coverage = 0.4;
  // Shared shader-side offsets (single node objects shared by all materials).
  private readonly _uWind: ReturnType<typeof uniform>;
  private readonly _uEvolve: ReturnType<typeof uniform>;
  private readonly _uWorldOffset: ReturnType<typeof uniform>;
  private readonly _uTime: ReturnType<typeof uniform>;
  private readonly _uFadeStart: ReturnType<typeof uniform>;
  private readonly _uFadeEnd: ReturnType<typeof uniform>;
  private readonly _uNoiseScale: ReturnType<typeof uniform>;
  private readonly _uExtinction: ReturnType<typeof uniform>;
  /** Diagnostic view (0 = off, 1 = chord-midpoint rNorm/baseNoise/boundGate
   *  as RGB) — flip via `setDebugView(true)` from a probe/console. */
  private readonly _uDebug: ReturnType<typeof uniform>;
  private readonly _uWindDir!: ReturnType<typeof uniform>;
  /** Active étages (see setFormation / formationForWeather). */
  private _formation!: CloudLayerSpec[];
  /** False until the first re-stream — births before it are the initial fill, not condensation. */
  private _everStreamed = false;

  constructor(opts: CloudMeshFieldOptions) {
    const c = opts.config ?? {};
    this._cfg = {
      fieldRadius: c.fieldRadius ?? 9000,
      cellSize: c.cellSize ?? 700,
      maxClouds: c.maxClouds ?? 40,
      baseAltitude: c.baseAltitude ?? 750,
      baseJitter: c.baseJitter ?? 40,
      radiusMin: c.radiusMin ?? 220,
      radiusMax: c.radiusMax ?? 520,
      noiseScale: c.noiseScale ?? 0.0038,
      weatherScale: c.weatherScale ?? 0.0001,
      extinction: c.extinction ?? 0.04,
      steps: c.steps ?? 20,
      sunSteps: c.sunSteps ?? 2,
      fadeStartRatio: c.fadeStartRatio ?? 0.72,
      windX: c.windX ?? 0,
      windZ: c.windZ ?? 0,
      windTimeScale: c.windTimeScale ?? 1,
      evolveSpeed: c.evolveSpeed ?? 1.5,
      growSeconds: c.growSeconds ?? 40,
      lodFarStep: c.lodFarStep ?? 2.5,
      impostors: c.impostors ?? {},
    };
    this._baseNoise = opts.baseNoise;
    this._detailNoise = opts.detailNoise;
    const wImg = opts.weatherMap.image as { data: Uint8Array; width: number };
    this._weatherData = wImg.data;
    this._weatherSize = wImg.width;

    this.uniforms = {
      sunDirection: uniform(vec3(0, 1, 0)),
      sunColor: uniform(vec3(1, 1, 1)),
      sunIlluminance: uniform(5),
      ambientBottom: uniform(0.3),
      ambientTop: uniform(1.0),
      coverage: uniform(0.4),
      densityScale: uniform(1),
    };
    this._uWind = uniform(vec3(0, 0, 0));
    this._uEvolve = uniform(vec3(0, 0, 0));
    this._uWorldOffset = uniform(vec3(0, 0, 0));
    this._uTime = uniform(0);
    this._uFadeStart = uniform(this._cfg.fieldRadius * this._cfg.fadeStartRatio);
    this._uFadeEnd = uniform(this._cfg.fieldRadius);
    this._uNoiseScale = uniform(this._cfg.noiseScale);
    this._uExtinction = uniform(this._cfg.extinction);
    this._uDebug = uniform(0);
    // Wind direction (unit XZ) for cirrus streak alignment — real cirrus
    // hair aligns with the upper-level wind.
    const wl = Math.hypot(this._cfg.windX, this._cfg.windZ);
    this._uWindDir = uniform(new THREE.Vector2(
      wl > 1e-6 ? this._cfg.windX / wl : 1, wl > 1e-6 ? this._cfg.windZ / wl : 0));
    // Default formation: a single cumulus étage at the configured base.
    this._formation = [
      { kind: 'cumulus', baseAltitude: this._cfg.baseAltitude, coverage: 1, share: 1 },
    ];

    // Pooled meshes — one closed unit HEMISPHERE (dome + base cap) + one
    // material each. The flat base runs through the ball's equator (2026-09-10):
    // a bound whose bottom is a point held no flat-based turret off its axis,
    // so bases were a narrow cap and every outline a lozenge. All materials
    // share the global uniform nodes, so per-frame updates are single-point.
    const dome = new THREE.SphereGeometry(1, 24, 12, 0, Math.PI * 2, 0, Math.PI / 2);
    const cap = new THREE.CircleGeometry(1, 24);
    cap.rotateX(Math.PI / 2); // faces −y: outward for the underside
    const geo = mergeGeometries([dome, cap], false) ?? dome;
    dome.dispose(); cap.dispose();
    // Frustum grace margin: the analytic bound is EXACT, so a cloud used to
    // vanish the very frame its sphere left the view — right at the screen
    // edge, which read as turn-dependent popping. Shared geometry: one
    // inflate covers the whole pool.
    geo.computeBoundingSphere();
    if (geo.boundingSphere) geo.boundingSphere.radius = 1.15;
    for (let i = 0; i < this._cfg.maxClouds; i++) {
      const u: MeshCloudUniforms = {
        center: uniform(new THREE.Vector3(0, -10000, 0)),
        radius: uniform(300),
        flatten: uniform(0.6),
        params: uniform(new THREE.Vector3(1, 1, 0)),
        seed: uniform(new THREE.Vector3()),
        birth: uniform(-1000),
        death: uniform(1e9),
        grow: uniform(3),
        lod: uniform(1),
        lobes: uniformArray(Array.from({ length: MAX_LOBES }, () => new THREE.Vector4(0, -10, 0, 0)), 'vec4'),
      };
      const material = this._buildMaterial(u);
      const mesh = new THREE.Mesh(geo, material);
      mesh.frustumCulled = true;
      mesh.visible = false;
      mesh.renderOrder = 10; // after opaques; among transparents three sorts by center depth
      this.group.add(mesh);
      this._pool.push({ mesh, u, cellKey: null, dyingUntil: 0 });
    }
    this.group.name = 'CloudMeshField';
  }

  get enabled(): boolean { return this._enabled; }

  /** Number of pool meshes currently assigned to a cell (debug/verify). */
  get activeCount(): number {
    let n = 0;
    for (const e of this._pool) if (e.cellKey) n++;
    return n;
  }

  setEnabled(on: boolean): void {
    this._enabled = on;
    this.group.visible = on;
  }

  setCoverage(v: number): void {
    this._coverage = Math.max(0, Math.min(1, v));
    this.uniforms.coverage.value = this._coverage;
    // Re-stream only when coverage crosses a QUANTIZED bucket. The runtime
    // drives this every frame with a continuously-lerped value; NaN-ing the
    // latch unconditionally turned the designed cell-boundary re-stream into
    // a 60 Hz re-roll — the "clouds pop when I turn the camera" field bug
    // (2026-08-27): every frame could silently retire a visible cloud and
    // seed another. The shader uniform still tracks the smooth value.
    const bucket = Math.round(this._coverage * 25); // 0.04-wide steps
    if (bucket !== this._covBucket) {
      this._covBucket = bucket;
      this._lastCellX = Number.NaN;
    }
  }

  /**
   * Set the active étages (see `formationForWeather` for the weather-driven
   * default). Layers re-stream on the next update; per-genus morphology and
   * shading come from `CLOUD_ARCHETYPES`.
   */
  setFormation(f: CloudFormation): void {
    if (!f.layers.length) return;
    this._formation = f.layers.map((l) => ({ ...l }));
    this._lastCellX = Number.NaN;
  }

  get formation(): CloudLayerSpec[] { return this._formation; }

  setSunDirection(dir: { x: number; y: number; z: number }): void {
    (this.uniforms.sunDirection.value as THREE.Vector3).set(dir.x, dir.y, dir.z);
  }

  setSunColor(col: { r: number; g: number; b: number }): void {
    (this.uniforms.sunColor.value as THREE.Vector3).set(col.r, col.g, col.b);
  }

  setDensityScale(v: number): void {
    this.uniforms.densityScale.value = Math.max(0, v);
  }

  /** Diagnostic shader view: RGB = rNorm / base-noise R / boundary gate at
   *  the chord midpoint (probe/console tool — see the clouddiag doctrine). */
  setDebugView(on: boolean): void {
    this._uDebug.value = on ? 1 : 0;
  }

  /**
   * Per-frame drive. `cam`/`scenePos` are RENDER-space (three.js) positions;
   * the field works in ABSOLUTE world space (render − scenePos), matching the
   * CloudRenderer rebase convention so clouds never re-arrange on FP64 snaps.
   */
  update(dt: number, cam: THREE.Vector3, scenePos: THREE.Vector3): void {
    if (!this._enabled) return;
    this._elapsed += dt * this._cfg.windTimeScale;
    this._windX = this._cfg.windX * this._elapsed;
    this._windZ = this._cfg.windZ * this._elapsed;
    this._evolve = this._cfg.evolveSpeed * this._elapsed;
    (this._uWind.value as THREE.Vector3).set(this._windX, 0, this._windZ);
    // Net-zero evolution direction (0.62, −0.79) — wind-incommensurate, same
    // convention as the deck; base drifts +, detail −.
    (this._uEvolve.value as THREE.Vector3).set(this._evolve * 0.62, 0, this._evolve * -0.79);
    (this._uWorldOffset.value as THREE.Vector3).set(scenePos.x, 0, scenePos.z);
    this._uTime.value = this._elapsed;

    const camAbsX = cam.x - scenePos.x;
    const camAbsZ = cam.z - scenePos.z;

    // Re-stream only when the camera crosses a cell boundary (or coverage
    // changed and reset the latch).
    const cellX = Math.floor(camAbsX / this._cfg.cellSize);
    const cellZ = Math.floor(camAbsZ / this._cfg.cellSize);
    if (cellX !== this._lastCellX || cellZ !== this._lastCellZ) {
      this._lastCellX = cellX;
      this._lastCellZ = cellZ;
      this._restream(camAbsX, camAbsZ);
    }

    // Mesh positions hold the ABSOLUTE center — the rebase convention: scene
    // children carry absolute coords and the scene's own transform
    // (scene.position = −origin) maps them to render space. Pre-adding
    // scenePos here applied the shift TWICE (matrixWorld = abs + 2·scenePos):
    // near spawn that displaced every raster sphere ~|scenePos| from its
    // shader-side analytic sphere (misaligned volume sampling — an orb-mess
    // co-cause), and after a real rebase it pushed the meshes hundreds of km
    // out of the frustum entirely.
    for (const e of this._pool) {
      if (!e.cellKey) {
        // A dying mesh keeps rendering its fade-out; once done, park it.
        if (e.dyingUntil > 0 && this._elapsed >= e.dyingUntil) {
          e.dyingUntil = 0;
          e.mesh.visible = false;
          (e.u.center.value as THREE.Vector3).set(0, -10000, 0);
          // A mesh came free for a cell that missed out: re-stream next frame.
          if (this._starved) this._lastCellX = Number.NaN;
        }
        continue;
      }
      const c = e.u.center.value as THREE.Vector3;
      e.mesh.position.set(c.x, c.y, c.z);
      // Distance LOD: a cloud at the field's edge spans a few dozen pixels —
      // march it with a longer step (fewer live samples; the loop's guard
      // skips the rest).
      const d = Math.hypot(c.x - camAbsX, c.z - camAbsZ);
      const t = Math.min(1, Math.max(0, (d - this._cfg.fieldRadius * 0.35) / (this._cfg.fieldRadius * 0.65)));
      e.u.lod.value = 1 + (this._cfg.lodFarStep - 1) * t;
    }
  }

  /** What the impostor layer shares with the field (uniform NODES — one driver surface). */
  get shared(): {
    uniforms: CloudMeshField['uniforms'];
    wind: ReturnType<typeof uniform>; evolve: ReturnType<typeof uniform>; worldOffset: ReturnType<typeof uniform>;
    time: ReturnType<typeof uniform>; fadeStart: ReturnType<typeof uniform>; fadeEnd: ReturnType<typeof uniform>;
    noiseScale: ReturnType<typeof uniform>; windDir: ReturnType<typeof uniform>;
    baseNoise: THREE.Data3DTexture; weather: { data: Uint8Array; size: number };
    cfg: { fieldRadius: number; cellSize: number; weatherScale: number; fadeStartRatio: number; impostors: CloudImpostorLayerConfig | false };
    formation: CloudLayerSpec[]; coverage: number;
  } {
    // One cached object, refreshed in place: the impostor layer reads this
    // EVERY frame and the literal used to allocate four objects a call.
    let s = this._shared;
    if (!s) {
      s = this._shared = {
        uniforms: this.uniforms,
        wind: this._uWind, evolve: this._uEvolve, worldOffset: this._uWorldOffset, time: this._uTime,
        fadeStart: this._uFadeStart, fadeEnd: this._uFadeEnd, noiseScale: this._uNoiseScale, windDir: this._uWindDir,
        baseNoise: this._baseNoise, weather: { data: this._weatherData, size: this._weatherSize },
        cfg: { fieldRadius: this._cfg.fieldRadius, cellSize: this._cfg.cellSize, weatherScale: this._cfg.weatherScale, fadeStartRatio: this._cfg.fadeStartRatio, impostors: this._cfg.impostors },
        formation: this._formation, coverage: this._coverage,
      };
    }
    s.weather.data = this._weatherData; s.weather.size = this._weatherSize;
    s.formation = this._formation; s.coverage = this._coverage;
    return s;
  }
  private _shared: CloudMeshField['shared'] | null = null;

  /** Recompute the per-étage cell→mesh mapping around (camAbsX, camAbsZ). */
  private _restream(camAbsX: number, camAbsZ: number): void {
    // Normalize pool shares, then gather each layer's desired cells with its
    // genus cell size, its own weather-sampling offset (so étages don't stack
    // identically), and near-bias only for the FIRST (low) layer.
    const shareSum = this._formation.reduce((s, l) => s + Math.max(0, l.share), 0) || 1;
    const desired: Array<{ cell: DesiredCell; spec: CloudLayerSpec; arch: CloudArchetype }> = [];
    this._formation.forEach((spec, li) => {
      const arch = CLOUD_ARCHETYPES[spec.kind];
      const budget = Math.max(1, Math.floor(
        this._cfg.maxClouds * (Math.max(0, spec.share) / shareSum)));
      const layerCov = Math.max(0, Math.min(1, this._coverage * spec.coverage));
      if (layerCov < 0.02) return;
      const cells = computeDesiredCells(
        camAbsX, camAbsZ, layerCov,
        {
          fieldRadius: this._cfg.fieldRadius,
          cellSize: this._cfg.cellSize * arch.cellScale,
          maxClouds: budget,
          weatherScale: this._cfg.weatherScale,
          weatherOffset: { x: li * 3777.7, z: li * -2444.3 },
          nearBias: li === 0,
          keyPrefix: `L${li}:`,
          sticky: this._heldKeys,
        },
        { data: this._weatherData, size: this._weatherSize },
      );
      for (const cell of cells) desired.push({ cell, spec, arch });
    });
    const wanted = new Set(desired.map((d) => d.cell.key));

    // Release meshes whose cell fell out of the desired set — as a FADE, not
    // a cut (the one-frame `visible = false` was half of the popping bug; only
    // birth ever ramped). A dying mesh keeps rendering until its death fade
    // completes, and is only then recycled.
    const free: PoolEntry[] = [];
    for (const e of this._pool) {
      if (e.cellKey && !wanted.has(e.cellKey)) {
        e.cellKey = null;
        e.dyingUntil = this._elapsed + CloudMeshField.FADE_OUT_S;
        e.u.death.value = this._elapsed;
      }
      if (!e.cellKey && e.dyingUntil <= this._elapsed) free.push(e);
    }

    // Assign free meshes to new cells. Cells beyond the fade are already
    // invisible; nearer assignments fade in over ~3 s via uBirth.
    let starved = false;
    for (const { cell: d, spec, arch } of desired) {
      if (this._pool.some((e) => e.cellKey === d.key)) continue;
      if (free.length === 0) { starved = true; break; }
      const e = free.pop()!;
      const cellSize = this._cfg.cellSize * arch.cellScale;
      // ONE derivation for the volumetric mesh and the far sprite of a cell
      // (position, size, character, seed, turret layout) — the LOD hand-off
      // swaps representations of the SAME cloud.
      const cell = deriveCloudCell(d.cx, d.cz, cellSize, spec, arch, { data: this._weatherData, size: this._weatherSize }, this._cfg.weatherScale);
      const { absX, absZ, shape } = cell;
      e.cellKey = d.key;
      e.dyingUntil = 0;
      // The mesh sits at the BASE (the hemisphere's equator) and spans the dome height.
      (e.u.center.value as THREE.Vector3).set(absX, shape.baseY, absZ);
      e.u.radius.value = shape.radius;
      e.u.flatten.value = shape.flatten;
      (e.u.params.value as THREE.Vector3).set(cell.params.x, cell.params.y, cell.params.z);
      (e.u.seed.value as THREE.Vector3).set(cell.seed.x, cell.seed.y, cell.seed.z);
      const arr = e.u.lobes.array as THREE.Vector4[];
      for (let i = 0; i < MAX_LOBES; i++) { const l = cell.lobes[i]; arr[i].set(l.x, l.y, l.z, l.r); }
      e.u.birth.value = this._elapsed;
      e.u.death.value = 1e9;
      // CONDENSATION vs FADE: a cell born inside the visible field of an
      // already-streamed sky (the coverage rose, the regime changed) grows
      // over growSeconds; the first fill and the far-edge arrivals (which
      // cross-fade with the impostor layer) keep the 3 s fade.
      const inView = this._everStreamed && d.dist < this._cfg.fieldRadius * this._cfg.fadeStartRatio;
      e.u.grow.value = inView ? this._cfg.growSeconds : 3;
      e.mesh.scale.set(shape.radius, shape.height, shape.radius);
      e.mesh.visible = true;
      // ABSOLUTE position — the scene transform maps it to render space
      // (see update()).
      e.mesh.position.set(absX, shape.baseY, absZ);
    }
    this._starved = starved;
    this._everStreamed = true;
    // Refresh the hysteresis sticky set to what actually holds a cloud now.
    this._heldKeys.clear();
    for (const e of this._pool) if (e.cellKey) this._heldKeys.add(e.cellKey);
  }

  dispose(): void {
    for (const e of this._pool) {
      const m = e.mesh.material;
      (Array.isArray(m) ? m : [m]).forEach((mm) => mm.dispose());
    }
    // The pool shares ONE merged dome geometry — released once; the group
    // was scene.add()ed by CloudModule and stayed an orphan child of the
    // scene after every teardown (2026-09-12).
    this._pool[0]?.mesh.geometry.dispose();
    this.group.clear();
    this.group.removeFromParent();
    this._pool.length = 0;
  }

  // ─── TSL material ─────────────────────────────────────────────────────────

  private _buildMaterial(u: MeshCloudUniforms): THREE.NodeMaterial {
    const S = this.uniforms;
    const baseNoiseTex = this._baseNoise;
    const detailNoiseTex = this._detailNoise;
    const STEPS = this._cfg.steps;
    const SUN_STEPS = this._cfg.sunSteps;
    const uWind = this._uWind;
    const uEvolve = this._uEvolve;
    const uWorldOffset = this._uWorldOffset;
    const uTime = this._uTime;
    const uFadeStart = this._uFadeStart;
    const uFadeEnd = this._uFadeEnd;
    const uNoiseScale = this._uNoiseScale;
    const uExtinction = this._uExtinction;
    const uDebug = this._uDebug;
    const uWindDir = this._uWindDir;

    // TSL helpers as PLAIN functions (NOT Fn-wrapped) — the CloudRenderer
    // gotcha: nested Fn parameter substitution silently drops args in WGSL.
    // Render-space sphere center: u.center is ABSOLUTE world (stable across
    // FP64 rebase snaps); the ray/positions here are RENDER space, so add
    // worldOffset (= scene.position). Without this the analytic sphere sits
    // at the wrong place whenever the origin has rebased (>10 km from spawn)
    // — invisible at spawn where scene.position ≈ 0, catastrophic later.
    // u.center is the cloud's BASE point (the hemisphere's equator) — absolute.
    const centerR = (): any => vec3(u.center as never).add(uWorldOffset);
    // Semi-axes (m): rx = rz = radius, y = the DOME HEIGHT (2·radius·flatten —
    // humilis wider than tall). All shape math runs in "shape space"
    // (positions ÷ r3): the unit ball with the flat base through y = 0 and the
    // top at y = 1; only the upper half is cloud.
    const hgt = (): any => (u.radius as any).mul(u.flatten).mul(2);
    const r3 = (): any => vec3(u.radius as never, hgt(), u.radius as never);
    // Turret union (shape space): polynomial smooth-min over the lobes (the
    // CPU mirror is CloudLobes.lobeUnionSdf), plus the radius of the NEAREST
    // lobe — its scale sets the erosion band. Unrolled, branch-free.
    const lobeUnion = (rel: any): { sdf: any; rNear: any } => {
      let dS: any = float(10);
      let dRaw: any = float(10);
      let rNear: any = float(0.5);
      for (let i = 0; i < MAX_LOBES; i++) {
        const c = (u.lobes as any).element(int(i));
        const live = step(float(0.001), c.w);
        const di = mix(float(10), length(rel.sub(c.xyz)).sub(c.w), live);
        const h = max(float(LOBE_SMIN_K).sub(abs(dS.sub(di))), float(0)).div(LOBE_SMIN_K);
        dS = min(dS, di).sub(h.mul(h).mul(LOBE_SMIN_K * 0.25));
        rNear = mix(rNear, c.w, step(di, dRaw));
        dRaw = min(dRaw, di);
      }
      return { sdf: dS, rNear };
    };

    const sampleDensity = (p: any): any => {
      // Absolute world position (undo the origin rebase) for the noise UVs.
      const pAbs = p.sub(uWorldOffset);

      // Volume shape: the NOISE defines the puff; the sphere is only a bound.
      // The first cut multiplied the noise by a smooth radial envelope
      // (env = 1−r²), which made the ENVELOPE the shape: along tangential
      // rays the outer shell accumulates enough optical depth to draw the
      // bounding sphere as a hard circle (the "clouds with spheres around
      // them" report, worst at dusk/night against a dark sky). Instead a
      // SUBTRACTIVE boundary guard rises steeply over the outer shell, so
      // density is guaranteed 0 well inside the bound and the silhouette is
      // carved out of the noise — lumpy, never a circle.
      // The guard must kill the max possible noise (puffed ≤ 1.35) WITHOUT
      // help from the coverage term: at storm coverage the (1−cov) gate
      // collapsed and the old 1.45-amplitude guard only won in the outermost
      // shell — the 0.55–0.9·r band filled in and the volume drew as a soft
      // BALL (the night "orb around each cloud" report). 1.6 by 0.85·r beats
      // every possible noise value standalone (margin 0.25) while matching
      // the old interior extent at normal coverage (gate@0.6·r ≈ 0.61 vs the
      // old 0.82 — slightly fuller mid-shell, still noise-carved).
      const rel = p.sub(centerR()).div(r3()); // shape space (unit ball, base at y = 0)
      // THE TURRET GUARD (2026-09-10, "all pill-shaped"): the subtractive
      // boundary guard used to follow the radial distance of ONE ellipsoid, so
      // every silhouette was a convex lozenge whatever the noise did. It now
      // follows the smooth union of the cloud's turret lobes — rising over the
      // outer ~45 % of the nearest lobe's radius to 1.6 (≥ max noise + margin),
      // so density is 0 outside the union and the outline is carved from the
      // noise inside a LUMPY bound: several rounded tops of different heights
      // on one flat base, never a pill.
      const { sdf, rNear } = lobeUnion(rel);
      const sdfNorm = sdf.div(max(rNear, float(0.12)));
      const hLocal = clamp(rel.y, float(0), float(1));
      // FLAT BASE at the condensation level: the plane y = 0 of shape space
      // (the hemisphere's equator — a wide flat underside, the strongest
      // cumulus cue); a sharp ramp over the bottom 12 % of the dome. The tops
      // are the turrets'; a last soft cap keeps the noise inside the ball.
      const vertWin = smoothstep(float(0.01), float(0.12), rel.y)
        .mul(float(1).sub(smoothstep(float(0.86), float(1.0), rel.y)));

      // Wind-aligned noise anisotropy (u.params.z): the along-wind coordinate
      // is divided by (1 + stretch), elongating features into streaks — real
      // cirrus is "hair-like", combed along the upper wind. stretch 0 is the
      // identity, so cumulus/stratocumulus pay only the rotation arithmetic.
      const stretchDiv = float(1).add(vec3(u.params as never).z);
      const along = pAbs.x.mul(uWindDir.x).add(pAbs.z.mul(uWindDir.y)).div(stretchDiv);
      const cross = pAbs.z.mul(uWindDir.x).sub(pAbs.x.mul(uWindDir.y));
      const pAbsS = vec3(
        along.mul(uWindDir.x).sub(cross.mul(uWindDir.y)),
        pAbs.y,
        along.mul(uWindDir.y).add(cross.mul(uWindDir.x)),
      );

      // CLOUD-RELATIVE FREQUENCY: the base period is ~0.6 of the cloud's
      // radius (263 m at r 450 — about three florets across a lobe) — a fixed world period left a 120 m turret
      // seeing half a noise period, i.e. a smoothly inflated ball. Still
      // world-anchored (wind scroll, evolution, the per-cloud seed).
      const freq = uNoiseScale.mul(float(450).div(max(float(u.radius as never), float(80))));
      // Base noise: world-anchored + per-cloud seed + wind scroll + the
      // POSITIVE half of the net-zero evolution drift.
      const uvwBase = pAbsS.add(u.seed).add(uWind).add(uEvolve).mul(freq);
      const baseSample = texture3D(baseNoiseTex, uvwBase);
      const midFBM = baseSample.g.mul(0.625)
        .add(baseSample.b.mul(0.25))
        .add(baseSample.a.mul(0.125));
      const dilated = clamp(
        baseSample.r.sub(midFBM.sub(1).mul(0.5)), float(0), float(1));
      // Second octave (2.3×) and the detail texture (4×, the NEGATIVE
      // evolution half — net-zero): florets at ~0.4, ~0.2 and ~0.1 of a lobe.
      const base2 = texture3D(baseNoiseTex, uvwBase.mul(2.3).add(vec3(0.37, 0.11, 0.73)));
      const uvwDetail = pAbsS.add(u.seed).add(uWind).sub(uEvolve).mul(freq).mul(4);
      const detailSample = texture3D(detailNoiseTex, uvwDetail);
      const detailFBM = detailSample.r.mul(0.625)
        .add(detailSample.g.mul(0.25))
        .add(detailSample.b.mul(0.125));

      // THE CAULIFLOWER CARVE (2026-09-10, the "balloon cluster" pictures):
      // the lobe union's SDF is pushed OUTWARD by a contrast-stretched
      // two-octave fBm, so the surface retreats from the lobe skin by up to
      // 0.55 of the lobe radius where the noise is low and stays at the skin
      // where it peaks — florets within florets, the inverted-Worley billow
      // of the Nubis line. A gate on the plain SDF with the noise merely
      // MODULATING density moved the surface a tenth of a radius and every
      // turret drew as a smooth sphere. The carve only ever retreats inward,
      // so the density stays inside the mesh bound (the M5 doctrine holds).
      // Florets come from the two LOW octaves only (features ≈ a lobe and
      // half a lobe); with the 4× detail in the carve the contrast stretch
      // amplified it into grainy speckle (the second pictures) — the detail
      // texture only erodes the edges below.
      const fbm = baseSample.r.mul(0.62).add(base2.r.mul(0.38));
      const floret = smoothstep(float(0.33), float(0.67), fbm); // contrast: distinct florets
      // CONDENSATION: a cloud born in view starts with its surface retreated a
      // further 0.5 radius (small cores on the base) and grows out over
      // u.grow seconds until the florets reach the lobe skins.
      const growT = clamp(uTime.sub(u.birth).div(u.grow), float(0), float(1));
      const condense = float(1).sub(growT).mul(0.5);
      // 0.55 r deep: at 0.75 the troughs cut through to the lobe cores and a
      // cloud fell apart into drifting pieces (the third pictures).
      const sdfC = sdfNorm.add(float(1).sub(floret).mul(0.55)).add(condense);
      const boundGate = smoothstep(float(-0.4), float(0.05), sdfC).mul(1.6);

      const puffed = dilated.mul(vertWin.mul(1.4));

      // Coverage gate + boundary guard, both SUBTRACTIVE (Schneider remap
      // form, same family as the deck). Mesh clouds only spawn in
      // high-coverage cells, so the in-volume coverage gate is looser
      // (×1.35) than the deck's or the cores would thin out. cov is capped
      // at 0.9 so the noise gate (1−cov ≥ 0.1) NEVER fully collapses — in a
      // storm the interiors must stay noise-carved, not solid (the other
      // half of the orb report). The sphere-edge kill is boundGate's job
      // alone (see above), coverage-independent.
      const cov = clamp(S.coverage.mul(1.35), float(0), float(0.9));
      const gate = float(1).sub(cov).add(boundGate);
      const preErode = clamp(puffed.sub(gate), float(0), float(1));

      // Detail erosion (wispy at the base, billowy at the top).
      const erodeAmt = mix(
        detailFBM, float(1).sub(detailFBM), clamp(hLocal.mul(2), float(0), float(1)));
      // Edge-wispy erosion (Nubis doctrine: wispy at the faint edges, billowy
      // in the cores): erode hard where density is LOW, gently where high —
      // fringes shred into wisps while cores stay solid cauliflower.
      const erodeScale = mix(float(0.45), float(0.15),
        clamp(preErode.mul(3), float(0), float(1)))
        .mul(vec3(u.params as never).y); // genus erosion (cirrus shreds hard)
      const eroded = clamp(preErode.sub(erodeAmt.mul(erodeScale)), float(0), float(1));

      // Genus density (stratocumulus > 1 ⇒ self-shadowed gray deck;
      // cirrus ≪ 1 ⇒ translucent veil).
      return eroded.mul(S.densityScale).mul(vec3(u.params as never).x);
    };

    const hg = (cosTheta: any, g: any): any => {
      const g2 = g.mul(g);
      const denom = pow(
        max(float(1).add(g2).sub(g.mul(cosTheta).mul(2)), float(1e-4)),
        float(1.5),
      );
      return float(1).sub(g2).div(denom).mul(1.0 / (4.0 * Math.PI));
    };

    const sunCone = (p: any): any => {
      const L = normalize(S.sunDirection);
      const od = float(0).toVar('od');
      // Step scales with the cloud's VERTICAL thickness so the cone actually
      // exits the volume: the old fixed 60 m × 2 steps probed 120 m into
      // 220-560 m clouds — sun transmittance ≈ 1 everywhere, so undersides
      // never darkened and every cloud read as a flat white ball. Reaching
      // ~1 vertical semi-axis restores the dark-base / bright-top gradient
      // that makes cumulus read as 3D.
      const coneStep = hgt().mul(0.5).max(float(50));
      Loop({ start: uint(0), end: uint(SUN_STEPS), type: 'uint' }, ({ i }) => {
        const sp = p.add(L.mul(coneStep.mul(float(i).add(0.5))));
        od.addAssign(sampleDensity(sp).mul(uExtinction).mul(coneStep));
      });
      return od;
    };

    const marchNode = Fn(() => {
      // In a MESH material the ambient TSL camera accessors are the real
      // scene camera (the post-quad pitfall does not apply here).
      const ro = cameraPosition;
      // BackSide sphere ⇒ positionWorld is the sphere EXIT surface point.
      const rd = normalize(positionWorld.sub(ro));

      // Analytic ray-ELLIPSOID (render space; centerR() rebase-corrects the
      // absolute cell center). Shape space: divide by the semi-axes — the
      // ellipsoid becomes a unit sphere; t stays in world metres because rd
      // is unit-length in world space.
      const oc = ro.sub(centerR()).div(r3());
      const rdS = rd.div(r3());
      const a2 = dot(rdS, rdS);
      const b = dot(oc, rdS);
      const cc = dot(oc, oc).sub(float(1));
      const disc = b.mul(b).sub(a2.mul(cc));

      const scat = vec3(0).toVar('scat');
      const alpha = float(0).toVar('alpha');

      If(disc.greaterThan(float(0)), () => {
        const sq = disc.sqrt();
        const t0 = b.negate().sub(sq).div(a2);
        const t1Full = b.negate().add(sq).div(a2);
        // Camera inside the volume → start at 0; then CLIP the chord to the
        // dome: the mesh is the ball's upper half, so the base plane cuts the
        // chord where the ray crosses y = base (a rising ray enters there, a
        // falling ray leaves there).
        const tStart = max(t0, float(0)).toVar('tStart');
        const t1 = t1Full.toVar('tEnd');
        const tPlane = centerR().y.sub(ro.y).div(rd.y);
        If(rd.y.greaterThan(float(1e-5)), () => { tStart.assign(max(tStart, tPlane)); })
          .ElseIf(rd.y.lessThan(float(-1e-5)), () => { t1.assign(min(t1, tPlane)); });
        const centerDist = distance(ro, centerR());
        // NOTE: edges in ascending order — smoothstep(edge0>edge1) is
        // undefined in WGSL (the reversed form happened to work on GLSL).
        const distFade = float(1).sub(smoothstep(uFadeStart, uFadeEnd, centerDist));
        const ageFade = clamp(uTime.sub(u.birth).div(3), float(0), float(1));
        // Retirement mirror of the birth ramp: fades a released cloud out
        // over FADE_OUT_S instead of the old one-frame visible=false cut.
        const deathFade = float(1).sub(clamp(uTime.sub(u.death).div(2.5), float(0), float(1)));
        const lifeFade = ageFade.mul(deathFade);
        const marchGate = t1.greaterThan(tStart)
          .and(distFade.greaterThan(float(0.001)))
          .and(lifeFade.greaterThan(float(0.001)))
          .and(S.coverage.greaterThan(float(0.02)));

        If(marchGate, () => {
          const segLen = t1.sub(tStart);
          // Distance LOD: u.lod stretches the step (1 near → lodFarStep at the
          // field's edge); the loop's t-guard skips the samples past the exit.
          const stepLen = segLen.div(float(STEPS)).mul(u.lod);

          // Blue-noise march-start jitter (global LUT at 1:1 pixel:texel) —
          // scaled to the LOCAL step: in-volume paths are short and uniform,
          // so this is the de-banding scale with no dtOut-speckle ambiguity.
          const bnTex = getBlueNoiseTexture();
          const jitter = bnTex
            ? texture(bnTex, screenCoordinate.xy.div(float(bnTex.image.width))).r
            : fract(sin(dot(positionWorld.xy, vec2(12.9898, 78.233))).mul(43758.5453));
          const tCur = tStart.add(jitter.mul(stepLen)).toVar('tCur');
          const trans = float(1).toVar('trans');

          const cosTheta = dot(rd, normalize(S.sunDirection));
          // Forward-weighted dual HG (0.65/0.35): stronger forward lobe gives
          // the sun-side silver lining its punch; the back lobe keeps
          // down-sun clouds from going flat.
          const phase = hg(cosTheta, float(0.8)).mul(0.65)
            .add(hg(cosTheta, float(-0.5)).mul(0.35));

          Loop({ start: uint(0), end: uint(STEPS), type: 'uint' }, () => {
            If(tCur.lessThan(t1).and(trans.greaterThan(float(0.02))), () => {
              const p = ro.add(rd.mul(tCur));
              const d = sampleDensity(p);
              If(d.greaterThan(float(0.003)), () => {
                const sigmaE = d.mul(uExtinction);
                const stepOD = sigmaE.mul(stepLen);
                const stepT = exp(stepOD.negate());

                const sunOD = sunCone(p);
                const sunT = exp(sunOD.negate());

                const beer = exp(stepOD.negate());
                const powder = float(1).sub(exp(stepOD.mul(2).negate()));
                const energy = mix(float(1), powder, float(0.35)).mul(float(2).mul(beer));

                const rel = p.sub(centerR()).div(r3());
                const hLocal = clamp(rel.y, float(0), float(1)); // 0 base → 1 top
                // Ambient = SKY light (the Nubis direct+ambient split): the
                // shadowed underside is lit by the blue sky dome, so the base
                // ambient carries a cool tint; tops trend to neutral white.
                // Scalar ramp uniforms stay the WeatherModule night-ramp
                // surface; the tint is a constant chroma on top.
                const ambient = mix(
                  vec3(0.80, 0.90, 1.10).mul(S.ambientBottom),
                  vec3(1.0, 1.0, 1.0).mul(S.ambientTop),
                  hLocal,
                );

                const sunContrib = S.sunColor.mul(S.sunIlluminance).mul(sunT).mul(phase);
                const scatter = sunContrib.mul(energy)
                  .add(ambient);
                scat.addAssign(scatter.mul(trans).mul(float(1).sub(stepT)));
                trans.mulAssign(stepT);
              });
              tCur.addAssign(stepLen);
            });
          });

          // Fade BOTH the premultiplied radiance and the alpha — fading
          // alpha alone under One/OneMinusSrcAlpha turns the layer ADDITIVE
          // (rgb keeps adding while dst stops darkening): every birth/edge
          // fade glowed as a bright ghost ball at dusk. Scaling rgb and a by
          // the same factor is the correct premultiplied fade.
          const fade = distFade.mul(lifeFade);
          scat.mulAssign(fade);
          alpha.assign(float(1).sub(trans).mul(fade));
        });
      });

      // Diagnostic view (uDebug=1): chord-midpoint rNorm / raw base-noise R /
      // boundary gate as RGB — the clouddiag "see WHICH stage is wrong" tool.
      const dbg = vec3(0).toVar('dbgRgb');
      If(uDebug.greaterThan(float(0.5)).and(disc.greaterThan(float(0))), () => {
        const sq = disc.sqrt();
        const tMid = max(b.negate().sub(sq).div(a2), float(0))
          .add(b.negate().add(sq).div(a2)).mul(0.5);
        const pMid = ro.add(rd.mul(tMid));
        const relM = pMid.sub(centerR()).div(r3());
        const lu = lobeUnion(relM);
        const sdfM = lu.sdf.div(max(lu.rNear, float(0.12)));
        const gateM = smoothstep(float(-0.45), float(0.05), sdfM).mul(1.6);
        const uvwM = pMid.sub(uWorldOffset).add(u.seed).add(uWind).add(uEvolve).mul(uNoiseScale);
        const baseM = texture3D(baseNoiseTex, uvwM).r;
        dbg.assign(vec3(clamp(sdfM.negate(), float(0), float(1)), baseM, clamp(gateM, float(0), float(1))));
      });

      return mix(vec4(scat, alpha), vec4(dbg, float(0.9)), uDebug);
    });

    const out = marchNode();
    const material = new THREE.NodeMaterial();
    material.side = THREE.BackSide;
    material.transparent = true;
    material.depthWrite = false;
    // SCENE FOG OFF — NodeMaterial.fog defaults TRUE, and the scene's
    // exponential fog then paints the ENTIRE bounding sphere by its SURFACE
    // depth: a uniform fog-colored disc stamped over every cloud regardless
    // of the density math (THE "orb around each cloud" — brown at night
    // because the fog color is). A volumetric medium does its own extinction;
    // per-sample aerial perspective is the v2 hook.
    material.fog = false;
    // Premultiplied OVER — same composite contract as the post-pass clouds:
    // final = src.rgb + dst·(1 − src.a).
    material.blending = THREE.CustomBlending;
    material.blendSrc = THREE.OneFactor;
    material.blendDst = THREE.OneMinusSrcAlphaFactor;
    material.colorNode = vec4(out as never);
    markMrtOverlay(material); // a volume in front of a mountain must not rewrite the mountain's normal (AO / SSR)
    return material;
  }
}
